// src/components/Admin/FirmPortalDashboard.js
// ENHANCED: Time-series metrics, export, share, declining engagement alerts
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, List,
    ListItem, ListItemText, ListItemAvatar, Avatar, Divider, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select,
    MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Checkbox, FormControlLabel, IconButton, Tooltip, Stack
} from '@mui/material';
import {
    People as PeopleIcon,
    Business as BusinessIcon,
    QuestionAnswer as QuestionAnswerIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    CalendarMonth as CalendarIcon,
    Article as ArticleIcon,
    Notifications as NotificationsIcon,
    TrendingFlat as TrendingFlatIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType, BorderStyle } from 'docx';
import { toast } from 'react-toastify';
import ClientAlertsSection from '../Alerts/ClientAlertsSection';
import useOrganizationMetrics from '../../hooks/useOrganizationMetrics';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate an array of month objects for the last N months
 */
const getLastNMonths = (n) => {
    const months = [];
    const now = new Date();
    for (let i = n - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
            key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
            label: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
            fullLabel: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            start: new Date(date.getFullYear(), date.getMonth(), 1),
            end: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59)
        });
    }
    return months;
};

/**
 * Generate an array of quarter objects for the last N quarters
 */
const getLastNQuarters = (n) => {
    const quarters = [];
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const currentYear = now.getFullYear();
    
    for (let i = n - 1; i >= 0; i--) {
        let q = currentQuarter - i;
        let y = currentYear;
        while (q < 0) {
            q += 4;
            y -= 1;
        }
        const startMonth = q * 3;
        quarters.push({
            key: `${y}-Q${q + 1}`,
            label: `Q${q + 1} '${String(y).slice(2)}`,
            fullLabel: `Q${q + 1} ${y}`,
            start: new Date(y, startMonth, 1),
            end: new Date(y, startMonth + 3, 0, 23, 59, 59)
        });
    }
    return quarters;
};

/**
 * Check if a date falls within a period
 */
const isInPeriod = (date, period) => {
    if (!date) return false;
    const d = date.toDate ? date.toDate() : new Date(date);
    return d >= period.start && d <= period.end;
};

/**
 * Calculate engagement score for a client
 * Weighted: Questions (1.0), Follow-ups (1.5), Signals (0.5), Decision Memos (3.0)
 */
const calculateEngagementScore = (questions, signals, memos, followUps, lastActivityDate) => {
    const baseScore = (questions * 1.0) + (followUps * 1.5) + (signals * 0.5) + (memos * 3.0);
    
    // Recency multiplier
    if (!lastActivityDate) return baseScore * 0.5;
    const daysSinceActivity = Math.floor((new Date() - lastActivityDate) / (1000 * 60 * 60 * 24));
    let multiplier = 1.0;
    if (daysSinceActivity <= 7) multiplier = 1.2;
    else if (daysSinceActivity <= 30) multiplier = 1.0;
    else if (daysSinceActivity <= 60) multiplier = 0.8;
    else multiplier = 0.5;
    
    return Math.round(baseScore * multiplier * 10) / 10;
};

/**
 * Detect declining engagement (3+ consecutive periods of decline)
 */
const detectDecliningEngagement = (clientId, clientName, periodData, periods) => {
    if (periods.length < 3) return null;
    
    const recentPeriods = periods.slice(-4); // Last 4 periods
    const values = recentPeriods.map(p => periodData[p.key]?.[clientId]?.questions || 0);
    
    // Check for 3 consecutive declines
    let consecutiveDeclines = 0;
    for (let i = 1; i < values.length; i++) {
        if (values[i] < values[i - 1]) {
            consecutiveDeclines++;
        } else {
            consecutiveDeclines = 0;
        }
    }
    
    if (consecutiveDeclines >= 2) {
        return {
            clientId,
            clientName,
            trend: values,
            periods: recentPeriods.map(p => p.label)
        };
    }
    return null;
};

const FirmPortalDashboard = ({ 
    user, 
    orgData, 
    orgConfig,
    // NEW: Alert props passed from FirmPortal
    alerts = [],
    alertsLoading = false,
    onDismissAlert,
    onDismissAllAlerts,
    onRefetchAlerts,
    onNavigateToClient
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [metrics, setMetrics] = useState({
        totalSeats: 0,
        activeSeats: 0,
        totalClients: 0,
        totalFCFOs: 0,
        questionsThisMonth: 0,
        questionsTrend: 0,
        activeClients: 0,
        inactiveClients: 0
    });
    const [topFCFOs, setTopFCFOs] = useState([]);
    const [topClients, setTopClients] = useState([]);
    const [inactiveClients, setInactiveClients] = useState([]);
    const [anyClientHasAsana, setAnyClientHasAsana] = useState(false);

    // NEW: Time-series state
    const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'quarterly'
    const [periodCount, setPeriodCount] = useState(12); // 6, 12, or custom
    const [timeSeriesData, setTimeSeriesData] = useState({});
    const [periods, setPeriods] = useState([]);
    
    // NEW: Enhanced metrics state
    const [clientEngagementData, setClientEngagementData] = useState([]);
    const [fcfoEngagementData, setFcfoEngagementData] = useState([]);
    const [decliningEngagementAlerts, setDecliningEngagementAlerts] = useState([]);
    
    // NEW: Export modal state
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState('csv');
    const [exportFields, setExportFields] = useState({
        metricsMatrix: true,
        topClients: true,
        topFCFOs: true,
        decliningAlerts: true
    });
    const [isExporting, setIsExporting] = useState(false);
    
    // NEW: Share modal state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareData, setShareData] = useState({
        recipientEmail: '',
        recipientName: '',
        personalMessage: ''
    });
    const [isSharing, setIsSharing] = useState(false);
    const [shareErrors, setShareErrors] = useState({});
    
    const {
        metrics: cachedMetrics,
        loading: cachedLoading,
        isStale,
        isRefreshing,
        lastUpdatedDisplay,
        refreshMetrics,
        monthlyMetrics: cachedMonthlyMetrics,
        topClients: cachedTopClients,
        topFCFOs: cachedTopFCFOs,
        decliningAlerts: cachedDecliningAlerts,
        counters: cachedCounters
    } = useOrganizationMetrics(user?.organizationId);
    
    // Determine if we should use cached data
    const useCachedData = cachedMetrics && !cachedLoading;
    const inactiveThresholdDays = orgData?.inactiveClientThresholdDays || 30;

    const fcfoLabel = orgConfig?.terminology?.fcfo || 'Advisor';
    const fcfosLabel = orgConfig?.terminology?.fcfos || 'Advisors';
    
    // Compute periods based on view mode
    const computedPeriods = useMemo(() => {
        return viewMode === 'monthly' 
            ? getLastNMonths(periodCount) 
            : getLastNQuarters(Math.min(periodCount, 8));
    }, [viewMode, periodCount]);


    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!user?.organizationId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Fetch members
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const membersSnap = await getDocs(membersQuery);
                const members = membersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                const fcfos = members.filter(m => m.userPortalAccess !== false);
                const activeMembers = members.filter(m => m.active !== false);

                // Fetch clients
                const clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', user.organizationId)
                );
                const clientsSnap = await getDocs(clientsQuery);
                const clients = clientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Fetch ALL questions for time-series analysis
                const questionsQuery = query(
                    collection(db, 'questions'),
                    where('organizationId', '==', user.organizationId)
                );
                const questionsSnap = await getDocs(questionsQuery);
                const questions = questionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Fetch ALL business updates (SavvaiSignals)
                const updatesQuery = query(
                    collection(db, 'businessUpdates'),
                    where('organizationId', '==', user.organizationId)
                );
                const updatesSnap = await getDocs(updatesQuery);
                const updates = updatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Calculate inactive clients
                const now = new Date();
                const inactiveThreshold = new Date(now - inactiveThresholdDays * 24 * 60 * 60 * 1000);
                
                const clientsWithActivity = clients.map(client => {
                    const lastActivity = client.lastActivityAt?.toDate() || client.createdAt?.toDate();
                    const isInactive = lastActivity && lastActivity < inactiveThreshold;
                    const fcfo = fcfos.find(f => f.id === client.fcfoId);
                    return {
                        ...client,
                        isInactive,
                        fcfoName: fcfo?.name || fcfo?.email || 'Unknown',
                        lastActivityDate: lastActivity
                    };
                });

                const activeClients = clientsWithActivity.filter(c => c.isActive !== false && !c.isInactive);
                const inactiveClientsList = clientsWithActivity.filter(c => c.isActive !== false && c.isInactive);

                // ============================================================
                // TIME-SERIES CALCULATIONS
                // ============================================================
                const periodsToUse = computedPeriods;
                setPeriods(periodsToUse);

                // Initialize time-series data structure
                const tsData = {};
                const clientPeriodData = {}; // For declining engagement detection
                
                periodsToUse.forEach(period => {
                    // Count questions in this period
                    const periodQuestions = questions.filter(q => isInPeriod(q.createdAt, period));
                    const periodFollowUps = periodQuestions.filter(q => q.parentId || q.type === 'follow-up');
                    const periodUpdates = updates.filter(u => isInPeriod(u.createdAt, period));
                    const periodMemos = questions.filter(q => 
                        q.type === 'decision_memo' && isInPeriod(q.createdAt, period)
                    );
                    
                    // Count unique active clients in this period
                    const activeClientIds = new Set([
                        ...periodQuestions.map(q => q.clientId),
                        ...periodUpdates.map(u => u.clientId)
                    ].filter(Boolean));

                    tsData[period.key] = {
                        activeClients: activeClientIds.size,
                        questions: periodQuestions.length - periodFollowUps.length, // Exclude follow-ups from main count
                        followUps: periodFollowUps.length,
                        signals: periodUpdates.length,
                        decisionMemos: periodMemos.length
                    };

                    // Track per-client data for declining engagement
                    clientPeriodData[period.key] = {};
                    clientsWithActivity.forEach(client => {
                        const clientQuestions = periodQuestions.filter(q => q.clientId === client.id);
                        clientPeriodData[period.key][client.id] = {
                            questions: clientQuestions.length
                        };
                    });
                });

                // Use cached time-series data if available, otherwise use calculated
                if (useCachedData && cachedMonthlyMetrics && Object.keys(cachedMonthlyMetrics).length > 0) {
                    // Transform cached monthly metrics to match expected format
                    const cachedTsData = {};
                    periodsToUse.forEach(period => {
                        cachedTsData[period.key] = cachedMonthlyMetrics[period.key] || {
                            activeClients: 0,
                            questions: 0,
                            followUps: 0,
                            signals: 0,
                            decisionMemos: 0
                        };
                    });
                    setTimeSeriesData(cachedTsData);
                } else {
                    setTimeSeriesData(tsData);
                }

                // ============================================================
                // ENGAGEMENT SCORES & TOP 10 LISTS
                // ============================================================
                
                // Calculate per-client engagement data
                const clientEngagement = clientsWithActivity
                    .filter(c => c.isActive !== false)
                    .map(client => {
                        const clientQuestions = questions.filter(q => q.clientId === client.id && !q.parentId);
                        const clientFollowUps = questions.filter(q => q.clientId === client.id && (q.parentId || q.type === 'follow-up'));
                        const clientSignals = updates.filter(u => u.clientId === client.id);
                        const clientMemos = questions.filter(q => q.clientId === client.id && q.type === 'decision_memo');
                        
                        return {
                            ...client,
                            questionCount: clientQuestions.length,
                            followUpCount: clientFollowUps.length,
                            signalCount: clientSignals.length,
                            memoCount: clientMemos.length,
                            engagementScore: calculateEngagementScore(
                                clientQuestions.length,
                                clientSignals.length,
                                clientMemos.length,
                                clientFollowUps.length,
                                client.lastActivityDate
                            )
                        };
                    })
                    .sort((a, b) => b.engagementScore - a.engagementScore)
                    .slice(0, 10);

                // Use cached top clients if available
                if (useCachedData && cachedTopClients && cachedTopClients.length > 0) {
                    setClientEngagementData(cachedTopClients.map(c => ({
                        ...c,
                        id: c.clientId,
                        name: c.name,
                        questionCount: c.questions,
                        followUpCount: c.followUps,
                        signalCount: c.signals,
                        memoCount: c.memos,
                        engagementScore: c.score
                    })));
                } else {
                    setClientEngagementData(clientEngagement);
                }

                // Calculate per-FCFO engagement data
                const fcfoEngagement = fcfos.map(fcfo => {
                    const fcfoClients = clientsWithActivity.filter(c => c.fcfoId === fcfo.id && c.isActive !== false);
                    const fcfoQuestions = questions.filter(q => q.fcfoId === fcfo.id && !q.parentId);
                    const fcfoFollowUps = questions.filter(q => q.fcfoId === fcfo.id && (q.parentId || q.type === 'follow-up'));
                    const fcfoSignals = updates.filter(u => u.fcfoId === fcfo.id);
                    const fcfoMemos = questions.filter(q => q.fcfoId === fcfo.id && q.type === 'decision_memo');
                    
                    return {
                        ...fcfo,
                        clientCount: fcfoClients.length,
                        questionCount: fcfoQuestions.length,
                        followUpCount: fcfoFollowUps.length,
                        signalCount: fcfoSignals.length,
                        memoCount: fcfoMemos.length,
                        engagementScore: calculateEngagementScore(
                            fcfoQuestions.length,
                            fcfoSignals.length,
                            fcfoMemos.length,
                            fcfoFollowUps.length,
                            fcfo.lastLoginAt?.toDate()
                        )
                    };
                })
                .sort((a, b) => b.engagementScore - a.engagementScore)
                .slice(0, 10);

                // Use cached top FCFOs if available
                if (useCachedData && cachedTopFCFOs && cachedTopFCFOs.length > 0) {
                    setFcfoEngagementData(cachedTopFCFOs.map(f => ({
                        ...f,
                        id: f.userId,
                        name: f.name,
                        email: f.email,
                        clientCount: f.clients,
                        questionCount: f.questions,
                        followUpCount: f.followUps,
                        signalCount: f.signals,
                        memoCount: f.memos,
                        engagementScore: f.score
                    })));
                } else {
                    setFcfoEngagementData(fcfoEngagement);
                }
                // ============================================================
                // DECLINING ENGAGEMENT DETECTION
                // ============================================================
                const decliningAlerts = [];
                clientsWithActivity
                    .filter(c => c.isActive !== false)
                    .forEach(client => {
                        const alert = detectDecliningEngagement(
                            client.id,
                            client.name || client.displayName || 'Unknown',
                            clientPeriodData,
                            periodsToUse
                        );
                        if (alert) {
                            alert.fcfoName = client.fcfoName;
                            decliningAlerts.push(alert);
                        }
                    });
                
                // Use cached declining alerts if available
                if (useCachedData && cachedDecliningAlerts) {
                    setDecliningEngagementAlerts(cachedDecliningAlerts);
                } else {
                    setDecliningEngagementAlerts(decliningAlerts);
                }

                // ============================================================
                // LEGACY METRICS (for backward compatibility with metric cards)
                // ============================================================
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                const questionsThisMonth = questions.filter(q => 
                    q.createdAt?.toDate() >= startOfMonth
                ).length;

                const questionsLastMonth = questions.filter(q => {
                    const date = q.createdAt?.toDate();
                    return date >= startOfLastMonth && date < startOfMonth;
                }).length;

                const trend = questionsLastMonth > 0 
                    ? Math.round(((questionsThisMonth - questionsLastMonth) / questionsLastMonth) * 100)
                    : 0;

                // Legacy top FCFOs (for backward compatibility)
                const fcfoQuestionCounts = {};
                questions.forEach(q => {
                    if (q.fcfoId) {
                        fcfoQuestionCounts[q.fcfoId] = (fcfoQuestionCounts[q.fcfoId] || 0) + 1;
                    }
                });

                const topFCFOsList = fcfos
                    .map(f => ({ ...f, questionCount: fcfoQuestionCounts[f.id] || 0 }))
                    .sort((a, b) => b.questionCount - a.questionCount)
                    .slice(0, 5);

                // Legacy top clients
                const clientQuestionCounts = {};
                questions.forEach(q => {
                    if (q.clientId) {
                        clientQuestionCounts[q.clientId] = (clientQuestionCounts[q.clientId] || 0) + 1;
                    }
                });

                const topClientsList = clientsWithActivity
                    .filter(c => c.isActive !== false)
                    .map(c => ({ ...c, questionCount: clientQuestionCounts[c.id] || 0 }))
                    .sort((a, b) => b.questionCount - a.questionCount)
                    .slice(0, 5);

                setMetrics({
                    totalSeats: members.length,
                    activeSeats: activeMembers.length,
                    totalClients: clients.length,
                    totalFCFOs: fcfos.length,
                    questionsThisMonth,
                    questionsTrend: trend,
                    activeClients: activeClients.length,
                    inactiveClients: inactiveClientsList.length
                });

                setTopFCFOs(topFCFOsList);
                setTopClients(topClientsList);
                setInactiveClients(inactiveClientsList.slice(0, 5));
                
                // Check if any client has Asana connected
                const hasAsana = clients.some(c => 
                    c.asanaConnection?.lastSyncAt || 
                    c.asanaLastSyncAt || 
                    c.asanaOperationalBriefUpdatedAt
                );
                setAnyClientHasAsana(hasAsana);
                
                setLoading(false);

            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError(`Failed to load dashboard: ${err.message}`);
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user?.organizationId, inactiveThresholdDays, computedPeriods]);

    // Handle view client from alert - navigate to Clients tab
    const handleViewClient = (clientId) => {
        if (onNavigateToClient) {
            onNavigateToClient(clientId);
        }
    };

    // ============================================================
    // EXPORT FUNCTIONS
    // ============================================================
    
    const generateCSVContent = useCallback(() => {
        const rows = [];
        
        // Header info
        rows.push(`"Firm Portal Dashboard Export"`);
        rows.push(`"Generated:","${new Date().toLocaleString()}"`);
        rows.push(`"Organization:","${orgData?.name || 'N/A'}"`);
        rows.push(`"View:","${viewMode === 'monthly' ? 'Monthly' : 'Quarterly'}"`);
        rows.push('');
        
        if (exportFields.metricsMatrix) {
            // Time-series metrics table
            rows.push('"TIME-SERIES METRICS"');
            const headers = ['Metric', ...periods.map(p => `"${p.label}"`)];
            rows.push(headers.join(','));
            
            const metricsRows = [
                ['Active Clients', ...periods.map(p => timeSeriesData[p.key]?.activeClients || 0)],
                ['Questions', ...periods.map(p => timeSeriesData[p.key]?.questions || 0)],
                ['Follow-ups', ...periods.map(p => timeSeriesData[p.key]?.followUps || 0)],
                ['SavvaiSignals', ...periods.map(p => timeSeriesData[p.key]?.signals || 0)],
                ['Decision Memos', ...periods.map(p => timeSeriesData[p.key]?.decisionMemos || 0)]
            ];
            metricsRows.forEach(row => {
                rows.push(`"${row[0]}",${row.slice(1).join(',')}`);
            });
            rows.push('');
        }
        
        if (exportFields.topClients) {
            rows.push('"TOP 10 MOST ENGAGED CLIENTS"');
            rows.push('"Rank","Client","FCFO","Questions","Signals","Memos","Follow-ups","Score"');
            clientEngagementData.forEach((client, idx) => {
                rows.push(`${idx + 1},"${client.name || client.displayName || 'Unknown'}","${client.fcfoName}",${client.questionCount},${client.signalCount},${client.memoCount},${client.followUpCount},${client.engagementScore}`);
            });
            rows.push('');
        }
        
        if (exportFields.topFCFOs) {
            rows.push(`"TOP 10 MOST ACTIVE ${fcfosLabel.toUpperCase()}"`);
            rows.push(`"Rank","${fcfoLabel}","Clients","Questions","Signals","Memos","Follow-ups","Score"`);
            fcfoEngagementData.forEach((fcfo, idx) => {
                rows.push(`${idx + 1},"${fcfo.name || fcfo.email}",${fcfo.clientCount},${fcfo.questionCount},${fcfo.signalCount},${fcfo.memoCount},${fcfo.followUpCount},${fcfo.engagementScore}`);
            });
            rows.push('');
        }
        
        if (exportFields.decliningAlerts && decliningEngagementAlerts.length > 0) {
            rows.push('"DECLINING ENGAGEMENT ALERTS"');
            rows.push('"Client","FCFO","Trend (Recent Periods)"');
            decliningEngagementAlerts.forEach(alert => {
                rows.push(`"${alert.clientName}","${alert.fcfoName}","${alert.trend.join(' → ')}"`);
            });
        }
        
        return rows.join('\n');
    }, [periods, timeSeriesData, clientEngagementData, fcfoEngagementData, decliningEngagementAlerts, exportFields, viewMode, orgData]);

    const generateWordDocument = useCallback(async () => {
        const doc = new Document({
            sections: [{
                children: [
                    new Paragraph({
                        text: `${orgData?.name || 'Firm'} Dashboard Report`,
                        heading: HeadingLevel.TITLE,
                        spacing: { after: 200 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: 'Generated: ', bold: true }),
                            new TextRun({ text: new Date().toLocaleString() })
                        ],
                        spacing: { after: 100 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({ text: 'View: ', bold: true }),
                            new TextRun({ text: viewMode === 'monthly' ? 'Monthly' : 'Quarterly' })
                        ],
                        spacing: { after: 400 }
                    })
                ]
            }]
        });

        const sectionChildren = [];

         if (exportFields.metricsMatrix) {
            sectionChildren.push(new Paragraph({
                text: 'Time-Series Metrics',
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 200 }
            }));
            
            // Add period headers row
            sectionChildren.push(new Paragraph({
                children: [
                    new TextRun({ text: 'Periods: ', bold: true }),
                    new TextRun({ text: periods.map(p => p.label).join(' | ') })
                ],
                spacing: { after: 150 }
            }));
            
            // Add metrics as formatted paragraphs (tables are complex in docx)
            const metricNames = ['Active Clients', 'Questions', 'Follow-ups', 'SavvaiSignals', 'Decision Memos'];
            metricNames.forEach(metricName => {
                const key = metricName.toLowerCase().replace(/ /g, '').replace('savvaisignals', 'signals').replace('decisionmemos', 'decisionMemos').replace('activeclients', 'activeClients').replace('follow-ups', 'followUps');
                const values = periods.map(p => timeSeriesData[p.key]?.[key] || 0);
                sectionChildren.push(new Paragraph({
                    children: [
                        new TextRun({ text: `${metricName}: `, bold: true }),
                        new TextRun({ text: values.join(' | ') })
                    ],
                    spacing: { after: 100 }
                }));
            });
        }

        if (exportFields.topClients) {
            sectionChildren.push(new Paragraph({
                text: 'Top 10 Most Engaged Clients',
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 200 }
            }));
            
            clientEngagementData.forEach((client, idx) => {
                sectionChildren.push(new Paragraph({
                    children: [
                        new TextRun({ text: `${idx + 1}. ${client.name || 'Unknown'}`, bold: true }),
                        new TextRun({ text: ` — FCFO: ${client.fcfoName} | Questions: ${client.questionCount} | Signals: ${client.signalCount} | Memos: ${client.memoCount} | Score: ${client.engagementScore}` })
                    ],
                    spacing: { after: 80 }
                }));
            });
        }

        if (exportFields.topFCFOs) {
            sectionChildren.push(new Paragraph({
                text: `Top 10 Most Active ${fcfosLabel}`,
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 200 }
            }));
            
            fcfoEngagementData.forEach((fcfo, idx) => {
                sectionChildren.push(new Paragraph({
                    children: [
                        new TextRun({ text: `${idx + 1}. ${fcfo.name || fcfo.email}`, bold: true }),
                        new TextRun({ text: ` — Clients: ${fcfo.clientCount} | Questions: ${fcfo.questionCount} | Signals: ${fcfo.signalCount} | Score: ${fcfo.engagementScore}` })
                    ],
                    spacing: { after: 80 }
                }));
            });
        }

        if (exportFields.decliningAlerts && decliningEngagementAlerts.length > 0) {
            sectionChildren.push(new Paragraph({
                text: 'Declining Engagement Alerts',
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 200 }
            }));
            
            decliningEngagementAlerts.forEach(alert => {
                sectionChildren.push(new Paragraph({
                    children: [
                        new TextRun({ text: `⚠ ${alert.clientName}`, bold: true }),
                        new TextRun({ text: ` (${alert.fcfoName}) — Trend: ${alert.trend.join(' → ')}` })
                    ],
                    spacing: { after: 80 }
                }));
            });
        }

        doc.addSection({ children: sectionChildren });
        return await Packer.toBlob(doc);
    }, [periods, timeSeriesData, clientEngagementData, fcfoEngagementData, decliningEngagementAlerts, exportFields, viewMode, orgData]);

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const filenameBase = `firm_dashboard_${new Date().toISOString().split('T')[0]}`;
            let blob;
            let filename;

            if (exportFormat === 'csv') {
                const csvContent = generateCSVContent();
                blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                filename = `${filenameBase}.csv`;
            } else {
                blob = await generateWordDocument();
                filename = `${filenameBase}.docx`;
            }

            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success(`Dashboard exported as ${exportFormat.toUpperCase()}`);
            setExportModalOpen(false);
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // ============================================================
    // SHARE FUNCTIONS
    // ============================================================
    
    const validateShareForm = () => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!shareData.recipientEmail.trim()) {
            errors.recipientEmail = 'Email is required';
        } else if (!emailRegex.test(shareData.recipientEmail)) {
            errors.recipientEmail = 'Invalid email address';
        }
        
        if (!shareData.recipientName.trim()) {
            errors.recipientName = 'Name is required';
        }
        
        setShareErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleShare = async () => {
        if (!validateShareForm()) return;
        
        try {
            setIsSharing(true);
            
            const shareFirmPortalDashboard = httpsCallable(functions, 'shareFirmPortalDashboardByEmail');
            const result = await shareFirmPortalDashboard({
                recipientEmail: shareData.recipientEmail.trim(),
                recipientName: shareData.recipientName.trim(),
                personalMessage: shareData.personalMessage.trim(),
                dashboardData: {
                    orgName: orgData?.name || 'Firm',
                    viewMode,
                    periods: periods.map(p => ({ key: p.key, label: p.label })),
                    timeSeriesData,
                    topClients: clientEngagementData.slice(0, 10),
                    topFCFOs: fcfoEngagementData.slice(0, 10),
                    decliningAlerts: decliningEngagementAlerts,
                    generatedAt: new Date().toISOString()
                }
            });

            if (result.data.success) {
                toast.success(`Dashboard shared with ${shareData.recipientName}`);
                setShareModalOpen(false);
                setShareData({ recipientEmail: '', recipientName: '', personalMessage: '' });
            } else {
                throw new Error(result.data.error || 'Failed to share');
            }
        } catch (error) {
            console.error('Share failed:', error);
            toast.error(error.message || 'Failed to share dashboard');
        } finally {
            setIsSharing(false);
        }
    };

    // Helper to render trend indicator
    const renderTrendIndicator = (current, previous) => {
        if (previous === 0) return <TrendingFlatIcon fontSize="small" color="disabled" />;
        const change = ((current - previous) / previous) * 100;
        if (change > 5) return <TrendingUpIcon fontSize="small" color="success" />;
        if (change < -5) return <TrendingDownIcon fontSize="small" color="error" />;
        return <TrendingFlatIcon fontSize="small" color="disabled" />;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading dashboard...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {/* Header with Title and Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Overview of your firm's activity and engagement.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    {/* Refresh button with staleness indicator */}
                    {useCachedData && (
                        <Tooltip title={`Last updated: ${lastUpdatedDisplay}${isStale ? ' (stale)' : ''}`}>
                            <Button
                                variant="outlined"
                                onClick={refreshMetrics}
                                disabled={isRefreshing}
                                color={isStale ? 'warning' : 'inherit'}
                                startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                            >
                                {isRefreshing ? 'Refreshing...' : 'Refresh'}
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => setExportModalOpen(true)}
                    >
                        Export
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ShareIcon />}
                        onClick={() => setShareModalOpen(true)}
                    >
                        Share
                    </Button>
                    {/* TEMPORARY: Remove after backfill complete */}
                    {true && (
    <Button
                            variant="contained"
                            color="warning"
                            onClick={async () => {
                                if (!window.confirm('Run metrics backfill for all organizations?')) return;
                                try {
                                    const backfill = httpsCallable(functions, 'backfillAllMetrics');
                                    const result = await backfill({});
                                    console.log('Backfill result:', result.data);
                                    alert(`Backfill complete! Processed: ${result.data.results?.processed || 0} orgs`);
                                } catch (err) {
                                    console.error('Backfill failed:', err);
                                    alert(`Backfill failed: ${err.message}`);
                                }
                            }}
                        >
                            Run Backfill
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Period Selector */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <Typography variant="subtitle2" color="text.secondary">View:</Typography>
                    </Grid>
                    <Grid item>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => newMode && setViewMode(newMode)}
                            size="small"
                        >
                            <ToggleButton value="monthly">
                                <CalendarIcon sx={{ mr: 0.5 }} fontSize="small" />
                                Monthly
                            </ToggleButton>
                            <ToggleButton value="quarterly">
                                <CalendarIcon sx={{ mr: 0.5 }} fontSize="small" />
                                Quarterly
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Period Range</InputLabel>
                            <Select
                                value={periodCount}
                                label="Period Range"
                                onChange={(e) => setPeriodCount(e.target.value)}
                            >
                                <MenuItem value={6}>Last 6 {viewMode === 'monthly' ? 'Months' : 'Quarters'}</MenuItem>
                                <MenuItem value={12}>Last 12 {viewMode === 'monthly' ? 'Months' : 'Quarters'}</MenuItem>
                                {viewMode === 'monthly' && <MenuItem value={18}>Last 18 Months</MenuItem>}
                                {viewMode === 'monthly' && <MenuItem value={24}>Last 24 Months</MenuItem>}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs />
                    <Grid item>
                        <Typography variant="body2" color="text.secondary">
                            Showing {periods.length} {viewMode === 'monthly' ? 'months' : 'quarters'}: {periods[0]?.fullLabel} – {periods[periods.length - 1]?.fullLabel}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Cache Status Indicator */}
            {useCachedData && (
                <Alert 
                    severity={isStale ? 'warning' : 'info'} 
                    sx={{ mb: 2 }}
                    action={
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={refreshMetrics}
                            disabled={isRefreshing}
                        >
                            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                        </Button>
                    }
                >
                    {isStale 
                        ? `Dashboard data is from ${lastUpdatedDisplay}. Click refresh for latest data.`
                        : `Dashboard data updated ${lastUpdatedDisplay}.`
                    }
                </Alert>
            )}

            {/* NEW: Client Data Freshness Alerts Section */}
            {alerts.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <ClientAlertsSection
                        alerts={alerts}
                        loading={alertsLoading}
                        onDismiss={onDismissAlert}
                        onDismissAll={onDismissAllAlerts}
                        onViewClient={handleViewClient}
                        onRefetch={onRefetchAlerts}
                        isFirmPortal={true}
                        compact={true}
                    />
                </Box>
            )}

            {/* Metric Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <PeopleIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h4">{metrics.activeSeats}/{metrics.totalSeats}</Typography>
                                <Typography variant="body2" color="text.secondary">Active Seats</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'success.main' }}>
                                <BusinessIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h4">{metrics.totalClients}</Typography>
                                <Typography variant="body2" color="text.secondary">Total Clients</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'info.main' }}>
                                <QuestionAnswerIcon />
                            </Avatar>
                            <Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h4">{metrics.questionsThisMonth}</Typography>
                                    {metrics.questionsTrend !== 0 && (
                                        <Chip 
                                            size="small"
                                            icon={metrics.questionsTrend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                            label={`${metrics.questionsTrend > 0 ? '+' : ''}${metrics.questionsTrend}%`}
                                            color={metrics.questionsTrend > 0 ? 'success' : 'error'}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">Questions This Month</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 3, bgcolor: metrics.inactiveClients > 0 ? 'warning.lighter' : 'inherit' }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: metrics.inactiveClients > 0 ? 'warning.main' : 'grey.400' }}>
                                <WarningIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h4">{metrics.inactiveClients}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Inactive Clients ({inactiveThresholdDays}+ days)
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Time-Series Metrics Table */}
            <Paper sx={{ p: 2, mb: 4, overflowX: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                    Activity Over Time
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, minWidth: 140, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                    Metric
                                </TableCell>
                                {periods.map((period, idx) => (
                                    <TableCell 
                                        key={period.key} 
                                        align="center"
                                        sx={{ 
                                            minWidth: 70,
                                            fontWeight: 600,
                                            bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit'
                                        }}
                                    >
                                        {period.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Active Clients Row */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                                    Active Clients
                                </TableCell>
                                {periods.map((period, idx) => {
                                    const value = timeSeriesData[period.key]?.activeClients || 0;
                                    const prevValue = idx > 0 ? (timeSeriesData[periods[idx - 1].key]?.activeClients || 0) : value;
                                    return (
                                        <TableCell key={period.key} align="center" sx={{ bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                {value}
                                                {idx > 0 && renderTrendIndicator(value, prevValue)}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {/* Questions Row */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                                    Questions
                                </TableCell>
                                {periods.map((period, idx) => {
                                    const value = timeSeriesData[period.key]?.questions || 0;
                                    const prevValue = idx > 0 ? (timeSeriesData[periods[idx - 1].key]?.questions || 0) : value;
                                    return (
                                        <TableCell key={period.key} align="center" sx={{ bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                {value}
                                                {idx > 0 && renderTrendIndicator(value, prevValue)}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {/* Follow-ups Row */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                                    Follow-up Questions
                                </TableCell>
                                {periods.map((period, idx) => {
                                    const value = timeSeriesData[period.key]?.followUps || 0;
                                    const prevValue = idx > 0 ? (timeSeriesData[periods[idx - 1].key]?.followUps || 0) : value;
                                    return (
                                        <TableCell key={period.key} align="center" sx={{ bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                {value}
                                                {idx > 0 && renderTrendIndicator(value, prevValue)}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {/* SavvaiSignals Row */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                                    SavvaiSignals Delivered
                                </TableCell>
                                {periods.map((period, idx) => {
                                    const value = timeSeriesData[period.key]?.signals || 0;
                                    const prevValue = idx > 0 ? (timeSeriesData[periods[idx - 1].key]?.signals || 0) : value;
                                    return (
                                        <TableCell key={period.key} align="center" sx={{ bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                {value}
                                                {idx > 0 && renderTrendIndicator(value, prevValue)}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {/* Decision Memos Row */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                                    Decision Memos
                                </TableCell>
                                {periods.map((period, idx) => {
                                    const value = timeSeriesData[period.key]?.decisionMemos || 0;
                                    const prevValue = idx > 0 ? (timeSeriesData[periods[idx - 1].key]?.decisionMemos || 0) : value;
                                    return (
                                        <TableCell key={period.key} align="center" sx={{ bgcolor: idx === periods.length - 1 ? 'primary.lighter' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                {value}
                                                {idx > 0 && renderTrendIndicator(value, prevValue)}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Declining Engagement Alerts */}
            {decliningEngagementAlerts.length > 0 && (
                <Paper sx={{ p: 2, mb: 4, bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.main' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <NotificationsIcon color="warning" />
                        <Typography variant="h6" color="warning.dark">
                            Declining Engagement Alerts
                        </Typography>
                        <Chip label={decliningEngagementAlerts.length} size="small" color="warning" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These clients have shown declining activity over the last 3+ consecutive periods.
                    </Typography>
                    <Grid container spacing={2}>
                        {decliningEngagementAlerts.map(alert => (
                            <Grid item xs={12} sm={6} md={4} key={alert.clientId}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {alert.clientName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {fcfoLabel}: {alert.fcfoName}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            Questions:
                                        </Typography>
                                        {alert.trend.map((val, idx) => (
                                            <React.Fragment key={idx}>
                                                <Typography 
                                                    variant="body2" 
                                                    color={idx === alert.trend.length - 1 ? 'error.main' : 'text.primary'}
                                                    fontWeight={idx === alert.trend.length - 1 ? 600 : 400}
                                                >
                                                    {val}
                                                </Typography>
                                                {idx < alert.trend.length - 1 && (
                                                    <TrendingDownIcon fontSize="small" color="error" />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {alert.periods.join(' → ')}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}

            {/* Lists */}
            <Grid container spacing={3}>
                {/* Top 10 FCFOs - Enhanced */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Top 10 Most Active {fcfosLabel}</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {fcfoEngagementData.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No activity yet
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{fcfoLabel}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Clients</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Q&A</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Signals</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Memos</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Score</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fcfoEngagementData.map((fcfo, index) => (
                                            <TableRow 
                                                key={fcfo.id}
                                                sx={{ 
                                                    bgcolor: index < 3 ? 'success.lighter' : 'inherit',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                <TableCell>
                                                    <Avatar sx={{ 
                                                        bgcolor: index === 0 ? 'warning.main' : index < 3 ? 'primary.main' : 'grey.400', 
                                                        width: 28, 
                                                        height: 28,
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {index + 1}
                                                    </Avatar>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={index < 3 ? 600 : 400}>
                                                        {fcfo.name || fcfo.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">{fcfo.clientCount}</TableCell>
                                                <TableCell align="center">{fcfo.questionCount}</TableCell>
                                                <TableCell align="center">{fcfo.signalCount}</TableCell>
                                                <TableCell align="center">{fcfo.memoCount}</TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={fcfo.engagementScore} 
                                                        size="small" 
                                                        color={index < 3 ? 'success' : 'default'}
                                                        variant={index < 3 ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Top 10 Clients - Enhanced */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Top 10 Most Engaged Clients</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {clientEngagementData.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No activity yet
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{fcfoLabel}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Q&A</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Signals</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Memos</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Score</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clientEngagementData.map((client, index) => (
                                            <TableRow 
                                                key={client.id}
                                                sx={{ 
                                                    bgcolor: index < 3 ? 'success.lighter' : 'inherit',
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleViewClient(client.id)}
                                            >
                                                <TableCell>
                                                    <Avatar sx={{ 
                                                        bgcolor: index === 0 ? 'warning.main' : index < 3 ? 'success.main' : 'grey.400', 
                                                        width: 28, 
                                                        height: 28,
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {index + 1}
                                                    </Avatar>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={index < 3 ? 600 : 400}>
                                                        {client.name || client.displayName || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {client.fcfoName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">{client.questionCount}</TableCell>
                                                <TableCell align="center">{client.signalCount}</TableCell>
                                                <TableCell align="center">{client.memoCount}</TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={client.engagementScore} 
                                                        size="small" 
                                                        color={index < 3 ? 'success' : 'default'}
                                                        variant={index < 3 ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Inactive Clients Alert */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: inactiveClients.length > 0 ? 'warning.lighter' : 'inherit' }}>
                        <Typography variant="h6" gutterBottom>
                            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                            Recently Inactive
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List dense>
                            {inactiveClients.length === 0 ? (
                                <ListItem>
                                    <ListItemText 
                                        secondary="All clients are active!" 
                                        secondaryTypographyProps={{ color: 'success.main' }}
                                    />
                                </ListItem>
                            ) : (
                                inactiveClients.map((client) => (
                                    <ListItem key={client.id}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                                                <BusinessIcon fontSize="small" />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText 
                                            primary={client.name}
                                            secondary={`${client.fcfoName}`}
                                        />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Paper>
                </Grid>
           </Grid>

            {/* Export Modal */}
            <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Export Dashboard</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Export Format</InputLabel>
                            <Select
                                value={exportFormat}
                                label="Export Format"
                                onChange={(e) => setExportFormat(e.target.value)}
                            >
                                <MenuItem value="csv">CSV (.csv)</MenuItem>
                                <MenuItem value="word">Word Document (.docx)</MenuItem>
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle2" gutterBottom>
                            Include in Export:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportFields.metricsMatrix}
                                        onChange={(e) => setExportFields(prev => ({ ...prev, metricsMatrix: e.target.checked }))}
                                    />
                                }
                                label="Time-Series Metrics Table"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportFields.topClients}
                                        onChange={(e) => setExportFields(prev => ({ ...prev, topClients: e.target.checked }))}
                                    />
                                }
                                label="Top 10 Most Engaged Clients"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportFields.topFCFOs}
                                        onChange={(e) => setExportFields(prev => ({ ...prev, topFCFOs: e.target.checked }))}
                                    />
                                }
                                label={`Top 10 Most Active ${fcfosLabel}`}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportFields.decliningAlerts}
                                        onChange={(e) => setExportFields(prev => ({ ...prev, decliningAlerts: e.target.checked }))}
                                    />
                                }
                                label="Declining Engagement Alerts"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        disabled={isExporting}
                        startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                        {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Modal */}
            <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ShareIcon color="primary" />
                        Share Dashboard Report
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            The recipient will receive an email with the current dashboard metrics, top performers, and any alerts.
                        </Alert>

                        <TextField
                            fullWidth
                            label="Recipient Email"
                            type="email"
                            value={shareData.recipientEmail}
                            onChange={(e) => {
                                setShareData(prev => ({ ...prev, recipientEmail: e.target.value }));
                                if (shareErrors.recipientEmail) setShareErrors(prev => ({ ...prev, recipientEmail: '' }));
                            }}
                            error={!!shareErrors.recipientEmail}
                            helperText={shareErrors.recipientEmail}
                            sx={{ mb: 2 }}
                            disabled={isSharing}
                        />

                        <TextField
                            fullWidth
                            label="Recipient Name"
                            value={shareData.recipientName}
                            onChange={(e) => {
                                setShareData(prev => ({ ...prev, recipientName: e.target.value }));
                                if (shareErrors.recipientName) setShareErrors(prev => ({ ...prev, recipientName: '' }));
                            }}
                            error={!!shareErrors.recipientName}
                            helperText={shareErrors.recipientName}
                            sx={{ mb: 2 }}
                            disabled={isSharing}
                        />

                        <TextField
                            fullWidth
                            label="Personal Message (Optional)"
                            multiline
                            rows={3}
                            value={shareData.personalMessage}
                            onChange={(e) => setShareData(prev => ({ ...prev, personalMessage: e.target.value }))}
                            placeholder="Add a personal note to include with the dashboard..."
                            disabled={isSharing}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareModalOpen(false)} disabled={isSharing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleShare}
                        variant="contained"
                        disabled={isSharing}
                        startIcon={isSharing ? <CircularProgress size={16} /> : <ShareIcon />}
                    >
                        {isSharing ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FirmPortalDashboard;